import React, { useCallback, useState } from 'react'
import { useIntl } from 'react-intl'
import { Button, ErrorBlock, InfiniteScroll, List, Toast } from 'antd-mobile-v5'
import { useHistory } from 'react-router-dom'
import Header from '../../components/FormTools/FormHeader'
import InfiniteScrollContent from '../../components/FormTools/InfiniteScrollContent'
import { useMyPurchasesList } from '../../services/hooks'
import { formatDate, formatMoney } from '../../components/common/utils'

// 后端 payTime 是 yyyyMMddHHmmss 原始字符串（后端 9d1127d81 确认），dayjs 直接解析会
// Invalid Date 导致时间显示为空：正则拆字段拼接；非该形态（如已格式化的历史值）回退 dayjs
const formatPayTime = (payTime) => {
  const m = /^(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})$/.exec(String(payTime))
  if (!m) return formatDate(payTime, 'YYYY-MM-DD HH:mm')
  return `${m[1]}-${m[2]}-${m[3]} ${m[4]}:${m[5]}`
}

const MyPurchasesPage = () => {
  const { push } = useHistory()
  const { formatMessage, messages } = useIntl()
  const [hasMore, setHasMore] = useState(true)
  const [page, setPage] = useState(1)
  const [list, setList] = useState([])

  const {
    loading,
    error,
    runAsync: fetchPage,
  } = useMyPurchasesList({
    manual: true,
    onSuccess: (data) => {
      const content = data.content || []
      setList((val) => [...val, ...content])
      setHasMore(page < (data.totalPages || 1))
      setPage((p) => p + 1)
    },
    // 抑制全局错误 Modal：瞬时失败由 loadMore catch 与 ErrorBlock 分层兜底
    onError: () => true,
  })

  const loadMore = useCallback(async () => {
    if (loading) return
    try {
      await fetchPage(page)
    } catch (e) {
      // 分页中途失败：已有数据时轻量 Toast（首屏失败由 ErrorBlock + 重试按钮兜底）
      if (list.length > 0) {
        Toast.show({ icon: 'fail', content: formatMessage({ id: 'my-purchases-load-failed' }) })
      }
      // reject 抛给 InfiniteScroll 进入 failed 状态（可点击重试），避免失败页被触底检查无限重试
      throw e
    }
  }, [fetchPage, page, loading, list.length, formatMessage])

  // 首屏失败重试：失败页尚未递增，直接重拉当前页
  const handleRetry = useCallback(() => {
    fetchPage(page).catch(() => {})
  }, [fetchPage, page])

  // detailRoute 白名单校验：仅允许站内相对路径（单斜杠开头、非协议相对），异常值禁用点击防空白页
  // 白名单：仅允许站内路径（单斜杠开头、非协议相对、无反斜杠——URL 解析会把 \ 规范化为 /）
  const isSafeDetailRoute = (route) =>
    typeof route === 'string' && route.startsWith('/') && !route.startsWith('//') && !route.includes('\\')

  const handleClick = (item) => {
    if (isSafeDetailRoute(item.detailRoute)) {
      push(item.detailRoute)
    }
  }

  // i18n 无兜底：后端新增枚举时回退显示原值，避免把原始 key 渲染给用户
  const getBusinessTypeLabel = (type) =>
    messages[`my-purchases-type-${type}`] ? formatMessage({ id: `my-purchases-type-${type}` }) : type
  const getOrderStatusLabel = (status) =>
    messages[`my-purchases-status-${status}`] ? formatMessage({ id: `my-purchases-status-${status}` }) : status

  return (
    <>
      <Header text={formatMessage({ id: 'my-purchases' })} type="list" />
      {error && list.length === 0 ? (
        <ErrorBlock status="default" title={formatMessage({ id: 'my-purchases-load-failed' })} description="">
          <Button color="primary" onClick={handleRetry}>
            {formatMessage({ id: 'my-purchases-retry' })}
          </Button>
        </ErrorBlock>
      ) : (
        <>
          <List>
            {/* 订单列表 orderId 必返回；回退 index 会在列表重排时错位，直接以 orderId 为 key */}
            {list.map((item) => (
              <List.Item
                key={item.orderId}
                title={
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>{getBusinessTypeLabel(item.businessType)}</span>
                    <span style={{ fontSize: 12, color: '#999' }}>{getOrderStatusLabel(item.orderSt)}</span>
                  </div>
                }
                description={
                  <div>
                    <div>{item.goodsName}</div>
                    <div style={{ color: '#999', fontSize: 12 }}>
                      {item.payTime ? formatPayTime(item.payTime) : ''} · {formatMoney(item.orderAmt / 100, '¥')}
                    </div>
                  </div>
                }
                clickable={isSafeDetailRoute(item.detailRoute)}
                onClick={() => handleClick(item)}
              />
            ))}
          </List>
          <InfiniteScroll loadMore={loadMore} hasMore={hasMore} threshold={300}>
            {(hasMore, failed, retry) => <InfiniteScrollContent hasMore={hasMore} failed={failed} onRetry={retry} />}
          </InfiniteScroll>
        </>
      )}
    </>
  )
}

export default MyPurchasesPage
